"use client"

import { useState, useEffect, useCallback } from "react"
import { useToast } from "@/components/ui/use-toast"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Loader2, UserPlus, MoreHorizontal } from "lucide-react"
import { format } from "date-fns"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { AdmitPatientModal } from "./admit-patient-modal"
import { supabase } from "@/utils/supabase/client";

// Define the type for an admission record
export interface Admission {
  id: string;
  admission_date: string;
  room_number: string | null;
  bed_number: string | null;
  initial_diagnosis: string | null;
  patients: { name: string; mr_number: string } | null;
  doctors: { name: string; specialization: string | null } | null;
}


export function AdmittedPatientsList() {
  const [admissions, setAdmissions] = useState<Admission[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { toast } = useToast()

  const fetchAdmissions = useCallback(async () => {
    setIsLoading(true)
    try {
      const { data, error } = await supabase
        .from("admissions")
        .select(`
          id,
          admission_date,
          room_number,
          bed_number,
          initial_diagnosis,
          patients!inner ( name, mr_number ),
          doctors!inner ( name, specialization )
        `)
        .is('discharge_date', null);

      if (error) throw error;
      const transformedData = data.map(d => ({
        ...d,
        patients: Array.isArray(d.patients) ? d.patients[0] : d.patients,
        doctors: Array.isArray(d.doctors) ? d.doctors[0] : d.doctors,
      }))
      setAdmissions(transformedData as Admission[]);
    } catch (error) {
      toast({ title: "Error", description: "Could not load admitted patients.", variant: "destructive" })
    } finally {
      setIsLoading(false)
    }
  }, [toast])

  useEffect(() => {
    fetchAdmissions()
  }, [fetchAdmissions])

  const handleDischarge = async (id: string) => {
    if (!window.confirm("Are you sure you want to discharge this patient?")) return;

    try {
        const { error } = await supabase
            .from('admissions')
            .update({ discharge_date: new Date().toISOString() })
            .eq('id', id);

        if (error) throw error;
        
        toast({ title: "Patient Discharged", description: "The patient has been successfully discharged." });
        fetchAdmissions(); // Refresh the list
    } catch (error: any) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  }

  return (
    <>
      <AdmitPatientModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onAdmitSuccess={fetchAdmissions} />
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Currently Admitted</CardTitle>
          <Button onClick={() => setIsModalOpen(true)}>
            <UserPlus className="mr-2 h-4 w-4" /> Admit Patient
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div> : (
            <div className="border rounded-md">
                <Table>
                <TableHeader>
                    <TableRow>
                    <TableHead>Patient</TableHead>
                    <TableHead className="hidden md:table-cell">Room/Bed</TableHead>
                    <TableHead className="hidden lg:table-cell">Admitted On</TableHead>
                    <TableHead>Attending Doctor</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {admissions.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="h-24 text-center">No patients currently admitted.</TableCell></TableRow>
                    ) : (
                    admissions.map((admission) => (
                        <TableRow key={admission.id}>
                        <TableCell>
                            <div className="font-medium">{admission.patients?.name}</div>
                            <div className="text-sm text-muted-foreground">{admission.patients?.mr_number}</div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">{admission.room_number || 'N/A'} / {admission.bed_number || 'N/A'}</TableCell>
                        <TableCell className="hidden lg:table-cell">{format(new Date(admission.admission_date), 'PPp')}</TableCell>
                        <TableCell>
                            <div className="font-medium">{admission.doctors?.name}</div>
                            <div className="text-sm text-muted-foreground">{admission.doctors?.specialization}</div>
                        </TableCell>
                        <TableCell className="text-right">
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className="h-8 w-8 p-0">
                                        <span className="sr-only">Open menu</span>
                                        <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => alert('View Details clicked for ' + admission.patients?.name)}>View Details</DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleDischarge(admission.id)}>Discharge Patient</DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </TableCell>
                        </TableRow>
                    ))
                    )}
                </TableBody>
                </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  )
} 